import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { Article, ArticleFormData } from '@/lib/types/admin';

const COLLECTION = 'articles';

// Get all articles (for admin)
export const getArticles = async (): Promise<Article[]> => {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Article[];
};

// Get published articles only (for public)
export const getPublishedArticles = async (limitCount?: number): Promise<Article[]> => {
  let q = query(
    collection(db, COLLECTION),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc')
  );

  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Article[];
};

// Get featured articles
export const getFeaturedArticles = async (limitCount: number = 3): Promise<Article[]> => {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'published'),
    where('featured', '==', true),
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Article[];
};

// Get articles by category
export const getArticlesByCategory = async (category: string): Promise<Article[]> => {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'published'),
    where('category', '==', category),
    orderBy('publishedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Article[];
};

// Get single article by ID
export const getArticleById = async (id: string): Promise<Article | null> => {
  const docRef = doc(db, COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Article;
  }
  return null;
};

// Get single article by slug (for public pages)
export const getArticleBySlug = async (slug: string): Promise<Article | null> => {
  const q = query(collection(db, COLLECTION), where('slug', '==', slug));
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as Article;
  }
  return null;
};

// Create article
export const createArticle = async (
  data: ArticleFormData,
  userId: string
): Promise<string> => {
  const readTime = calculateReadTime(data.content);

  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    readTime,
    publishedAt: data.status === 'published' ? Timestamp.now() : null,
    createdBy: userId,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// Update article
export const updateArticle = async (
  id: string,
  data: Partial<ArticleFormData>,
  currentStatus?: string
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  const updateData: Record<string, unknown> = {
    ...data,
    updatedAt: Timestamp.now(),
  };

  // Calculate read time if content changed
  if (data.content) {
    updateData.readTime = calculateReadTime(data.content);
  }

  // Set publishedAt if status changed to published
  if (data.status === 'published' && currentStatus !== 'published') {
    updateData.publishedAt = Timestamp.now();
  }

  await updateDoc(docRef, updateData);
};

// Delete article
export const deleteArticle = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await deleteDoc(docRef);
};

// Toggle article featured status
export const toggleArticleFeatured = async (
  id: string,
  featured: boolean
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    featured,
    updatedAt: Timestamp.now(),
  });
};

// Upload article cover image
export const uploadArticleImage = async (file: File): Promise<string> => {
  const timestamp = Date.now();
  const fileName = `articles/${timestamp}-${file.name}`;
  const storageRef = ref(storage, fileName);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
};

// Delete article image
export const deleteArticleImage = async (imageUrl: string): Promise<void> => {
  try {
    const storageRef = ref(storage, imageUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting article image:', error);
  }
};

// Generate slug from title
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Calculate read time based on content
export const calculateReadTime = (content: string): number => {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(1, readTime);
};

// Get all unique categories from published articles
export const getArticleCategories = async (): Promise<string[]> => {
  const articles = await getPublishedArticles();
  const categories = [...new Set(articles.map((article) => article.category))];
  return categories;
};

// Get related articles (same category, excluding current)
export const getRelatedArticles = async (
  category: string,
  excludeId: string,
  limitCount: number = 3
): Promise<Article[]> => {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'published'),
    where('category', '==', category),
    orderBy('publishedAt', 'desc'),
    limit(limitCount + 1)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() } as Article))
    .filter((article) => article.id !== excludeId)
    .slice(0, limitCount);
};
